import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Skeleton,
  Fade,
  Zoom,
  useTheme,
} from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import { useResponsive } from '../../../hooks/useResponsive';
import { formatCurrency, formatNumber, getCurrentDate } from '../../../utils/formatters';

/* ------------------------------------------------------------------ */
/*  Single Statistics Card                                            */
/* ------------------------------------------------------------------ */
const StatCard = ({
  title,
  value,
  isLoading,
  isCurrency = false,
  showDate = false,
  icon: IconComponent,
  trend = null,          // { value: 12.5, direction: 'up' | 'down' }
  gradient = 'primary',
  delay = 0,
  suffix = '',           // Added suffix prop for percentage, etc.
}) => {
  const theme = useTheme();
  const { isMobile } = useResponsive();

  /* ---------- derived ---------- */
  const formattedValue = React.useMemo(() => {
    if (isLoading || value == null) return null;
    
    let formatted;
    if (isCurrency) {
      formatted = formatCurrency(value);
    } else {
      formatted = formatNumber(value);
    }
    
    // Add suffix if provided (e.g., for percentages)
    return suffix ? `${formatted}${suffix}` : formatted;
  }, [value, isCurrency, isLoading, suffix]);

  const gradients = {
    primary: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    success: `linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)`,
    info:    `linear-gradient(135deg, #2196F3 0%, #1565C0 100%)`,
    warning: `linear-gradient(135deg, #FF9800 0%, #E65100 100%)`,
    purple:  `linear-gradient(135deg, #9C27B0 0%, #6A1B9A 100%)`,
    error:   `linear-gradient(135deg, #f44336 0%, #d32f2f 100%)`,
  };

  const iconColors = {
    primary: theme.palette.primary.main,
    success: '#4CAF50',
    info:    '#2196F3',
    warning: '#FF9800',
    purple:  '#9C27B0',
    error:   '#f44336',
  };

  // Determine if value indicates good or bad performance
  const getPerformanceColor = (trendDirection) => {
    // For revenue, winnings, customers - up is good
    if (['revenue', 'totalWin', 'customers', 'totalBets', 'totalGames'].some(key => title.toLowerCase().includes(key.toLowerCase()))) {
      return trendDirection === 'up' ? '#4CAF50' : '#f44336';
    }
    // For win rate - up is good
    if (title.toLowerCase().includes('win rate') || title.toLowerCase().includes('percentage')) {
      return trendDirection === 'up' ? '#4CAF50' : '#f44336';
    }
    // Default to trend direction colors
    return trendDirection === 'up' ? '#4CAF50' : '#f44336';
  };

  /* ---------- render ---------- */
  return (
    <Zoom in={!isLoading} timeout={300} style={{ transitionDelay: `${delay}ms` }}>
      <Card
        sx={{
          height: '100%',
          minHeight: { xs: 130, sm: 145, md: 160, lg: 150 },
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          background: theme.palette.mode === 'dark'
            ? `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[900]} 100%)`
            : `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[100]} 100%)`,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: { xs: 2, sm: 2.5, md: 3 },
          transition: 'all 0.25s ease-out',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          // Touch-friendly: only hover effects on non-touch devices
          '@media (hover: hover)': {
            '&:hover': {
              transform: 'translateY(-3px)',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 8px 20px rgba(0,0,0,0.35)'
                : '0 8px 20px rgba(0,0,0,0.08)',
              '& .stat-icon': { transform: 'scale(1.08) rotate(3deg)' },
            },
          },
          // Active state for touch
          '&:active': {
            transform: 'scale(0.98)',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, right: 0,
            height: { xs: 3, sm: 4 },
            background: gradients[gradient],
            zIndex: 1,
          },
        }}
      >
        <CardContent
          sx={{
            p: { xs: 1.25, sm: 1.5, md: 2 },
            position: 'relative',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            '&:last-child': { pb: { xs: 1.25, sm: 1.5, md: 2 } },
          }}
        >
          {/* decorative circle - subtle */}
          <Box
            sx={{
              position: 'absolute',
              top: { xs: -25, md: -30 }, 
              right: { xs: -25, md: -30 },
              width: { xs: 60, md: 80 }, 
              height: { xs: 60, md: 80 },
              borderRadius: '50%',
              background: `${iconColors[gradient]}06`,
              zIndex: 0,
            }}
          />

          {/* header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              position: 'relative',
              zIndex: 1,
              gap: 0.5,
              mb: { xs: 0.5, sm: 1 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 }, flex: 1, minWidth: 0 }}>
              {IconComponent && (
                <Box
                  className="stat-icon"
                  sx={{
                    p: { xs: 0.5, sm: 0.75, md: 1 },
                    borderRadius: { xs: 1.5, sm: 2 },
                    background: `${iconColors[gradient]}12`,
                    transition: 'all 0.25s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <IconComponent 
                    sx={{ 
                      color: iconColors[gradient], 
                      fontSize: { xs: '1rem', sm: '1.2rem', md: '1.35rem' } 
                    }} 
                  />
                </Box>
              )}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: 'text.secondary',
                  fontSize: { xs: '0.65rem', sm: '0.72rem', md: '0.8rem' },
                  letterSpacing: 0.2,
                  lineHeight: 1.25,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  wordBreak: 'break-word',
                }}
              >
                {title}
              </Typography>
            </Box>

            {trend && (
              <Fade in timeout={500}>
                <Box
                  className="trend-indicator"
                  sx={{
                    display: { xs: 'none', sm: 'flex' }, // Hide on very small screens
                    alignItems: 'center',
                    gap: 0.25,
                    px: { xs: 0.5, sm: 0.75 },
                    py: 0.25,
                    borderRadius: 1,
                    background: trend.direction === 'up'
                      ? 'rgba(76,175,80,0.1)'
                      : 'rgba(244,67,54,0.1)',
                    transition: 'all 0.25s ease',
                    flexShrink: 0,
                  }}
                >
                  {trend.direction === 'up'
                    ? <TrendingUp sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, color: getPerformanceColor('up') }} />
                    : <TrendingDown sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, color: getPerformanceColor('down') }} />}
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: getPerformanceColor(trend.direction),
                      fontSize: { xs: '0.6rem', sm: '0.65rem' },
                    }}
                  >
                    {trend.value}%
                  </Typography>
                </Box>
              </Fade>
            )}
          </Box>

          {/* value */}
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              py: { xs: 0.25, sm: 0.5 },
              minHeight: { xs: 32, sm: 40, md: 48 },
            }}
          >
            {isLoading ? (
              <Skeleton
                variant="text"
                width="75%"
                height={{ xs: 28, sm: 36, md: 44 }}
                sx={{ borderRadius: 1 }}
              />
            ) : (
              <Fade in timeout={600}>
                <Typography
                  variant="h4"
                  component="div"
                  sx={{
                    fontWeight: 700,
                    background: gradients[gradient],
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    // Fluid typography that scales smoothly
                    fontSize: { 
                      xs: '1.1rem',     // Mobile
                      sm: '1.25rem',    // Small tablet
                      md: '1.4rem',     // Tablet
                      lg: '1.2rem',     // Desktop (smaller for 4-column)
                      xl: '1.35rem',    // XL (6-column)
                    },
                    lineHeight: 1.15,
                    letterSpacing: '-0.01em',
                    fontFamily: '"Inter","Roboto",sans-serif',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: '100%',
                  }}
                >
                  {formattedValue}
                </Typography>
              </Fade>
            )}
          </Box>

          {/* date indicator - only show on larger screens */}
          {showDate && !isLoading && (
            <Box sx={{ mt: 'auto', display: { xs: 'none', md: 'block' } }}>
              <Fade in timeout={900}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      background: iconColors[gradient],
                      animation: 'pulse 2s infinite',
                      '@keyframes pulse': {
                        '0%': { opacity: 1 },
                        '50%': { opacity: 0.4 },
                        '100%': { opacity: 1 },
                      },
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontSize: '0.6rem', fontWeight: 500 }}
                  >
                    {getCurrentDate()}
                  </Typography>
                </Box>
              </Fade>
            </Box>
          )}
        </CardContent>
      </Card>
    </Zoom>
  );
};

export default StatCard;
